import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Form, Input, Layout, Typography, message, theme } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authFetch } from '../../utils/api';
import { tokenManager } from '../../utils/tokenManager';

const { Content } = Layout;
const { Paragraph, Text, Title } = Typography;

type PasswordFormValues = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const ForcePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { token: themeToken } = theme.useToken();
  const [form] = Form.useForm<PasswordFormValues>();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: PasswordFormValues) => {
    setIsSubmitting(true);

    try {
      const response = await authFetch('/api/admin/auth/password', {
        method: 'PUT',
        body: JSON.stringify({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          refreshToken: tokenManager.getRefreshToken(),
        }),
      });
      const result = await response.json();

      if (result.code !== 0 || !result.data) {
        throw new Error(result.msg || '密碼更新失敗');
      }

      const { token, refreshToken, expiresIn } = result.data;
      tokenManager.setTokens(token, refreshToken, expiresIn, false);
      form.resetFields();
      messageApi.success('密碼已更新，正在進入後台');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const messageText =
        error instanceof Error ? error.message : '密碼更新失敗';
      messageApi.error(messageText);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: themeToken.colorBgLayout,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {messageContextHolder}
      <Content
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 24,
          width: '100%',
        }}
      >
        <div
          style={{
            width: 460,
            maxWidth: '100%',
            background: themeToken.colorBgContainer,
            borderRadius: themeToken.borderRadiusLG,
            borderLeft: `4px solid ${themeToken.colorWarning}`,
            boxShadow: themeToken.boxShadowTertiary,
            padding: '40px 36px 32px',
            boxSizing: 'border-box',
          }}
        >
          <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
            首次登入請先修改密碼
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            目前帳號仍處於臨時密碼狀態。在完成密碼更新前，後台功能會被鎖定。
          </Paragraph>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            autoComplete="off"
          >
            <Form.Item
              name="oldPassword"
              label="目前密碼"
              rules={[{ required: true, message: '請輸入目前密碼' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeToken.colorTextQuaternary }} />}
                placeholder="請輸入目前密碼"
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="新密碼"
              rules={[
                { required: true, message: '請輸入新密碼' },
                { min: 8, message: '新密碼至少 8 個字元' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeToken.colorTextQuaternary }} />}
                placeholder="請輸入新密碼"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="確認新密碼"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '請再次輸入新密碼' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('兩次輸入的新密碼不一致'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeToken.colorTextQuaternary }} />}
                placeholder="請再次輸入新密碼"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8, marginTop: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                block
                size="large"
              >
                更新密碼
              </Button>
            </Form.Item>
          </Form>

          <Text style={{ fontSize: 12, color: themeToken.colorTextQuaternary }}>
            更新完成後會簽發新的登入憑證，並解除後台存取限制。
          </Text>
        </div>
      </Content>
    </Layout>
  );
};
